<template>
    <b-overlay
        variant="white"
        :show="showLoading"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <b-row>
            <b-col md="6" lg="6" class="mb-2">
                <h2 class="float-left pr-1 mb-1">
                    {{ $t('Page(ForwardRequest):Component(Text):page-label', { customer: request.customer.title }) }}
                </h2>
            </b-col>
            <b-col md="6" lg="6" class="text-right">
                <div class="demo-inline-spacing float-right">
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        :disabled="isForwardButtonDisabled"
                        class="mt-0"
                        @click="forward"
                    >
                        {{ $t('Component(Button):forward') }}
                    </b-button>

                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="secondary"
                        class="mt-0"
                        @click="back"
                    >
                        {{ $t('Component(Button):back') }}
                    </b-button>
                </div>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-card no-body>
                    <div class="card-header">
                        <div class="col-md-6 pl-0">
                            <b-card-title>{{ $t('Page(ForwardRequest):Component(Text):products-component-label') }}</b-card-title>
                        </div>
                        <div class="col-md-6 control-elements pr-0"></div>
                    </div>
                    <Transition mode="out-in" name="fade">
                        <b-alert
                            variant="secondary"
                            show
                            class="m-2"
                            v-if="components.length === 0"
                        >
                            <div class="alert-body">
                                <span>{{ $t('Page(ForwardRequest):Component(Text):nothing-selected') }}</span>
                            </div>
                        </b-alert>
                            <b-table
                                :fields="fields"
                                :items="components"
                                responsive
                                v-if="components.length"
                            >

                                <template #cell(checkbox)="data">
                                   <b-form-checkbox
                                       v-if="!data.item.ongoingRequestDate"
                                       :value="true"
                                       v-model="data.item.selected"
                                       @input="onCheckboxChange(data.item)"
                                   />
                                </template>
                                <template #cell(id)="data">
                                    <LegalIdentity
                                        :vertical="true"
                                        :view="true"
                                        :identity="data.item.supplier.legalIdentity"
                                    />
                                </template>
                                <!-- A custom formatted column -->
                                <template #cell(name)="data">
                                    <div style="min-width: 150px;">
                                        {{ data.item.supplier.title }}
                                    </div>
                                </template>

                                <template #cell(productId)="data">
                                    <div v-if="data.item.hasIdentifier" style="min-width: 150px;">
                                        {{ data.item.supplierIdentifier }}
                                    </div>
                                    <div v-else>
                                        <b-form-input
                                            style="min-width: 150px;"
                                            :state="data.item.supplierIdentifier === '' && data.item.supplierName === '' ? false:null"
                                            :class="{'input-bg-warning' : data.item.supplierIdentifier === '' && data.item.supplierName === ''}"
                                            v-model="data.item.supplierIdentifier"
                                            :placeholder="$t('Page(ReceivedRequests):Component(Grid):field-product-id')"
                                        />
                                    </div>
                                </template>

                                <template #cell(productName)="data">
                                    <div v-if="data.item.hasName" style="min-width: 150px;">
                                        {{ data.item.supplierName }}
                                    </div>
                                    <div v-else>
                                        <b-form-input
                                            style="min-width: 150px;"
                                            :state="data.item.supplierIdentifier === '' && data.item.supplierName === '' ? false:null"
                                            :class="{'input-bg-warning' : data.item.supplierIdentifier === '' && data.item.supplierName === ''}"
                                            v-model="data.item.supplierName"
                                            :placeholder="$t('Page(ReceivedRequests):Component(Grid):field-product-name')"
                                        />
                                    </div>
                                </template>

                               <template #cell(productOrComponent)="data">
                                   {{ data.item.ourName }}
                               </template>

                                <template #cell(location)="data">
                                    {{ data.item.supplier.state.title }}
                                </template>

                                <template #cell(contactName)="data">
                                    {{ data.item.supplier.contact.name }}
                                </template>

                                <template #cell(contactEmail)="data">
                                    {{ data.item.supplier.contact.email }}
                                </template>

                               <template #cell(actions)="data">
                                   <b-button
                                       v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                                       variant="danger"
                                       class="btn-icon rounded-circle"
                                       @click="removeItem(data.item)"
                                       v-if="data.item.new"
                                   >
                                       <feather-icon icon="Trash2Icon" />
                                   </b-button>
                                   <span v-if="data.item.ongoingRequestDate">
                                       <b-button
                                           v-if="data.item.isEmailSent"
                                           v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                           variant="info"
                                           class="mt-0"
                                           size="sm"
                                           :disabled="disabledButtons.includes(data.item.id)"
                                           @click="sendEmail(data.item, 'button' + data.item.id)"
                                       >
                                            {{ $t('Page(SendRequest):Component(Button):resend-email') }}
                                        </b-button>
                                       <b-button
                                           v-else
                                           v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                           variant="info"
                                           class="mt-0"
                                           size="sm"
                                           :disabled="disabledButtons.includes(data.item.id)"
                                           @click="sendEmail(data.item, 'button' + data.item.id)"
                                       >
                                           {{ $t('Page(SendRequest):Component(Button):send-email') }}
                                       </b-button>
                                   </span>
                               </template>

                               <template #cell(ongoingRequest)="data">
                                   <div class="text-center">
                                       <div v-if="data.item.ongoingRequestDate">
                                           {{ data.item.ongoingRequestDate }}
                                       </div>
                                       <StatusBadge v-else-if="data.item.hasBeenApproved" type="sent" :status="statusApproved"/>
                                       <span v-else>-</span>
                                   </div>
                               </template>
                            </b-table>
                    </Transition>
                </b-card>
            </b-col>
        </b-row>
        <b-row class="row">
            <b-col>
                <RequestDetails
                    ref="requestDetails"
                    :request="request"
                    :hidden-legislations="hiddenLegislations"
                    :last-response-date="lastResponseDate"
                />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <SendRequestEmailExample
                    :supplier-name="emailExampleSupplierName"
                />
            </b-col>
        </b-row>
    </b-overlay>
</template>
<script>
import PurchasedProductInputSearch from "@core/components/product/PurchasedProductInputSearch";
import Ripple from 'vue-ripple-directive'
import { BCard, BCardText, BRow, BCol, BFormRadio, BButton, BFormCheckbox,
    BListGroup, BListGroupItem, BAlert, BCardTitle, BTable, BFormInput, BOverlay, BBadge
} from 'bootstrap-vue'
import {ProductApi} from '@core/api'
import {SupplierSearch, NewSupplierModal} from "@core/components/supply-chain";
import {IncludedProduct} from "@core/components/product";
import {ProductIdentifiers, ProductNames, ProductType, setNameOrId} from "@/libs/product";
import RequestDetails from "@/views/request/RequestDetails";
import RequestEmailOptions from "@core/components/common/RequestEmailOptions";
import SendRequestEmailExample from "@/views/product/SendRequestEmailExample";
import StatusBadge from "@/views/request/components/StatusBadge";
import {RequestStatus} from "@/libs/product";
import LegalIdentity from "@core/components/common/LegalIdentity.vue";
import {ProductProvider} from "@core/domain/product";
import backendResponse from "@core/mixins/ui/backend-response";

export default {
    mixins: [backendResponse],
    components: {
        LegalIdentity,
        BOverlay, BFormCheckbox, SendRequestEmailExample, BBadge, BCard, BCardText, BCardTitle, BFormInput,
        BRow, BCol, BButton, BListGroup, BListGroupItem, BAlert, BFormRadio, BTable, SupplierSearch, NewSupplierModal,
        IncludedProduct, RequestDetails, PurchasedProductInputSearch, RequestEmailOptions, StatusBadge,
    },
    directives: {
        Ripple
    },
    props: {
        id: {
            required: true
        },
        req: {
            type: Object,
            default() {
                return {
                    id: null,
                    legislations: []
                };
            }
        }
    },

    data() {
        return {
            r: {
                customer: {
                    title: 'Test'
                },
                verificationDocuments: [],
                product: {
                    suppliers: [],
                    includedProducts: [],
                    type: ProductType.PURCHASED
                },
                legislations: []
            },
            statusApproved: RequestStatus.APPROVED,
            idIncrement: 0,
            products: [],
            fields: [
                { key: 'checkbox', label: this.$t('Page(SendRequest):Component(Text):field-checkbox') },
                { key: 'productOrComponent', label: this.$t('Page(ForwardRequest):Component(Text):column-product-or-component') },
                { key: 'name', label: this.$t('Page(ForwardRequest):Component(SupplierSelector):column-supplier-name') },
                { key: 'productName', label: this.$t('Page(ForwardRequest):Component(SupplierSelector):column-product-name') },
                { key: 'productId', label: this.$t('Page(ForwardRequest):Component(SupplierSelector):column-product-id') },
                { key: 'id', label: this.$t('Component(SupplierSelector):field-vat-number') },
                { key: 'location', label: this.$t('Component(SupplierSelector):field-location') },
                { key: 'contactName', label: this.$t('Component(SupplierSelector):field-contact-name') },
                { key: 'contactEmail', label: this.$t('Component(SupplierSelector):field-contact-email') },
                // { key: 'ongoingRequest', label: 'Ongoing request' },
                { key: 'ongoingRequest', label: this.$t('Page(ForwardRequest):Component(Text):column-ongoing-request') },
                { key: 'actions', label: 'Actions'},
            ],
            showLoading: true,

            lastResponseDate: null,

            existedProducts: [],
            includedProducts: [],
            newProducts: [],
            disabledButtons: [],

            components: [],
            hiddenLegislations: []
        };
    },

    methods: {
        sendEmail(component) {
            this.disabledButtons.push(component.id)
            ProductApi.sendProductRequestEmail(parseInt(component.id)).then(() => {
                component.isEmailSent = true;
                let index = this.disabledButtons.findIndex(id => id === component.id);

                if (index >= 0) {
                    this.disabledButtons.splice(index, 1);
                }
            });
        },

        onCheckboxChange() {

        },

        prepareHiddenLegislations() {
            if (!this.shouldFilterForwardedLegislationsByApplicability) {
                this.hiddenLegislations = []
                return
            }

            if (this.r.product.legislations) {
                this.hiddenLegislations = Object.values(this.r.product.legislations)
                    .filter(l => !l.isApplicable)
                    .map(l => l.id);
            }
        },

        fetchProductRequest() {
            return ProductApi.getCustomerRequest(this.id).then(r => {
                this.r = r.data;

                this.prepareHiddenLegislations();
            });
        },

        removeItem(item) {
            let index = this.products.findIndex((p) => p.id === item.id);

            if (index >= 0) {
                this.products.splice(index, 1);
            }
        },

        showNewSupplierForm() {
            this.$refs['supplierForm'].showModal();
        },

        validate() {
            return this.components
                .filter(c => true === c.selected)
                .filter(c => c.supplierIdentifier === '' && c.supplierName === '')
                .length === 0;
        },

        forward() {
            if (this.validate()) {
                this.showLoading = true;

                const request = this.$refs.requestDetails.getData();
                if (this.$route.query.et) {
                    request.emailOptions = {
                        type: this.$route.query.et,
                        id: this.$route.query.eid
                    };
                }
                request.products = this.components.filter(c => true === c.selected).map(c => parseInt(c.id));
                const saveNameIdentifierComponents = this.components
                    .filter(c => true === c.selected && (c.hasIdentifier === false || c.hasName === false));

                let promise = new Promise((resolve) => resolve());
                if (saveNameIdentifierComponents.length > 0) {
                    promise = Promise.all(saveNameIdentifierComponents.map(c => this.saveComponentNameIdentifier(c)));
                }
                promise
                    .then(() => {
                        return ProductApi
                            .forwardRequest(this.id, request)
                            .then(r => {
                                this.showDefaultSuccessMessage(
                                    this.$t('Page(ForwardRequest):Component(RequestForwardedAck):text'),
                                    true,
                                    this.$t('Page(ForwardRequest):Component(RequestForwardedAck):title')
                                )
                                setTimeout(() => {
                                    this.$store.dispatch('refreshCounters');
                                    this.$router.push({name: 'sent-requests'})
                                }, 600);
                            })
                            .catch(this.handleBackendError);
                    })
                    .finally(() => {
                        this.showLoading = false;
                        this.sendRequestInProgress = false;
                    })
                ;
            }
        },

        back(){
            this.$router.go(-1);
        },

        saveComponentNameIdentifier(component) {
            return new Promise((resolve, reject) => {
                const product = ProductProvider(component.id);
                product
                    .getCard()
                    .then(card => {
                        if (component.supplierName) {
                            setNameOrId(card.names, ProductNames.SUPPLIERS_PRODUCT_NAME, component.supplierName);
                        }
                        if (component.supplierIdentifier) {
                            setNameOrId(card.identifiers, ProductIdentifiers.SUPPLIERS_PRODUCT_ID, component.supplierIdentifier);
                        }
                        product
                            .saveCard()
                            .then(resolve)
                            .catch(reject)
                    })
                    .catch(reject)
                ;
            });
        },

        initComponents(components) {
            components.forEach(c => {
                c.selected = !c.ongoingRequestDate;
                c.isEmailSent = undefined === c.isEmailSent ? false : c.isEmailSent;
            });
        },

        init() {
            this.fetchComponents();
            this.calculateLastResponseDate();
        },

        calculateLastResponseDate() {
            const requireDays = 10;
            if (this.request.daysToDeadLine < requireDays) {
                let date = new Date();
                date.setDate(date.getDate() + requireDays);
                this.lastResponseDate = date;
            } else {
                let days = this.request.daysToDeadLine - requireDays;
                this.lastResponseDate = this.$moment(this.request.validTo)
                    .subtract(days > requireDays ? requireDays : days, 'days')
                    .toDate();
            }
        },

        fetchComponents() {
            ProductApi
                .getComponentsToRequest(this.request.product.id)
                .then(r => {
                    this.initComponents(r.data);
                    r.data.forEach(component => {
                        component.hasIdentifier = true;
                        component.hasName = true;
                        if (component.supplierIdentifier === '') {
                            component.hasIdentifier = false;
                            component.supplierIdentifier = component.ourIdentifier;
                        }
                        if (component.supplierName === '') {
                            component.hasName = false;
                            component.supplierName = component.ourName;
                        }
                    });
                    this.components = r.data;
                })
                .finally(() => this.showLoading = false);
        },
    },

    computed: {
        isForwardButtonDisabled() {
            const selected = this.components.filter(c => true === c.selected);

            if (selected.length === 0) {
                return true;
            }

            return selected.filter(c => c.supplierIdentifier === '' && c.supplierName === '').length > 0;
        },

        emailExampleSupplierName() {
            if (this.products.length === 0) {
                return null;
            } else if (this.products.length === 1) {
                return this.products[0].supplier.title;
            }

            for (let i = 1; i < this.products.length; ++i) {
                if (this.products[0].supplier.id !== this.products[i].supplier.id) {
                    return null;
                }
            }

            return this.products[0].supplier.title;
        },

        request() {
            return this.req.id ? this.req : this.r;
        },

        hasSupplier() {
            return this.products.length > 0;
        },

        shouldFilterForwardedLegislationsByApplicability() {
            return false
        },
    },

    created() {
        if (null === this.req.id) {
            this.fetchProductRequest().then(() => this.init());
        } else {
            this.init();
        }
    }
}
</script>
